<template>
  <div class="flex flex-row gap-2 items-center">
    <Avatar
      :src="updatedFile || image"
      size="3xl"
      :class="{
        'outline-white outline outline-2 shadow-md': maxSmall,
      }"
    />
    <div class="flex flex-row gap-1">
      <Button
        variant="secondary"
        :label="$t('components.profile.avatar.button')"
        type="gray"
        :size="maxSmall ? 'md' : 'xs'"
        @click="$refs.fileInputRef.click()"
      >
      </Button>
      <input
        ref="fileInputRef"
        type="file"
        @change="handleFileSelect"
        :accept="acceptableTypes.join(',')"
        class="hidden"
      />
      <Button
        @click="removedFile"
        variant="secondary"
        type="destructive"
        icon
        :size="maxSmall ? 'md' : 'xs'"
      >
        <Icon name="delete" />
      </Button>
    </div>
  </div>
  <div v-if="errors.length" class="flex flex-col gap-1 text-red-500 mt-2">
    <div v-for="error in errors" :key="error">{{ error }}</div>
  </div>
</template>
<script setup>
import { Icon } from "@/SharedComponents/Icon";
import { Button } from "@/SharedComponents/Buttons";
import { Avatar } from "@/SharedComponents/Avatars";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useBreakpoints } from "@/Composables/useBreakpoints";

const props = defineProps({
  image: {
    type: String,
    default: null,
  },
  maxSize: {
    type: Number,
    default: 4 * 1024 * 1024,
  },
  acceptableTypes: {
    type: Array,
    default: () => ["image/png", "image/jpeg", "image/jpg"],
  },
});
const { maxSmall } = useBreakpoints();
const fileInputRef = ref();
const emit = defineEmits(["fileSelected", "fileRemoved"]);
const updatedFile = ref(null);
const errors = ref([]);
const { t } = useI18n();

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (!validFile(file)) {
    fileInputRef.value = null;
    return;
  }
  const reader = new FileReader();
  reader.onload = (e) => {
    updatedFile.value = e.target.result;
    emit("fileSelected", { file, url: e.target.result });
  };
  reader.readAsDataURL(file);
  // Allow selection of the same file again.
  event.target.value = null;
};

// basic validation for file
const validFile = (file) => {
  errors.value = [];
  if (file.size > props.maxSize) {
    errors.value.push(
      t("components.errors.files.upload.size_limit_exceeded", {
        maxSize: Math.floor(props.maxSize / 1024 / 1024) + "MB",
      })
    );
  }
  if (!props.acceptableTypes.includes(file.type)) {
    errors.value.push(
      t("components.errors.files.upload.unaccepted_type_selected", {
        acceptableTypes: props.acceptableTypes
          .map((el) => el.split("/").pop())
          .join(", "),
      })
    );
  }
  return !errors.value.length;
};

const removedFile = () => {
  updatedFile.value = null;
  errors.value = [];
  emit("fileRemoved");
};
</script>
